import * as React from "react";
import { useAction } from "convex/react";
import {
  AlertTriangle,
  BadgeCheck,
  ChevronDown,
  Clipboard,
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  SquarePen,
  UserRoundCog,
} from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/convexApi";
import { copyTextToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/utils";
import { CustomerProfileEditDialog } from "./CustomerProfileEditDialog";

export type RecoveryResult = {
  normalizedEmail: string;
  authUsers: Array<{
    _id: string;
    email: string | null;
    name: string | null;
    emailVerified: boolean | null;
    createdAt: number | string | null;
  }>;
  userProfiles: Array<{
    _id: string;
    userId: string | null;
    email: string | null;
    name: string | null;
    firstName: string | null;
    lastName: string | null;
    company: string | null;
    mainPhoneNumber: string | null;
    accountingEmail: string | null;
    orderingEmail: string | null;
    truckingEmail: string | null;
    role: string | null;
    updatedAt: number | null;
  }>;
  pendingProfiles: Array<{
    _id: string;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
  }>;
  emailEvents: Array<{
    _id: string;
    emailType: string;
    status: string;
    subject: string;
    createdAt: number;
    error: string | null;
  }>;
  summary: {
    authUserCount: number;
    userProfileCount: number;
    pendingProfileCount: number;
    emailVerified: boolean;
    profileLinked: boolean;
    canRepairProfile: boolean;
    canMarkEmailVerified: boolean;
    canResendVerification: boolean;
    canGenerateVerificationLink: boolean;
    canUsePasswordRecovery: boolean;
    resendWaitSeconds: number;
    deliveryBlocked: boolean;
    issues: string[];
  };
};

type RecoverySuggestion = {
  email: string;
  name: string | null;
  company: string | null;
  authAccountCount: number;
  profileCount: number;
  stagedSignupCount: number;
  emailVerified: boolean | null;
};

type SuggestionState = {
  query: string;
  status: "idle" | "loading" | "ready" | "error";
  items: RecoverySuggestion[];
};

type BusyAction =
  | "search"
  | "resend"
  | "verification-link"
  | "mark-verified"
  | "repair"
  | "password-email"
  | "password-link"
  | null;
type ConfirmationAction =
  "resend" | "mark-verified" | "repair" | "password-email" | null;
type Tone = "success" | "warning" | "danger" | "neutral";

function message(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function formatDate(value: number | string | null | undefined) {
  if (value === null || value === undefined) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function deliveryStatus(value: string): { label: string; tone: Tone } {
  switch (value.toLowerCase()) {
    case "sent":
      return { label: "Provider accepted", tone: "neutral" };
    case "delivered":
    case "opened":
    case "clicked":
      return {
        label: value.charAt(0).toUpperCase() + value.slice(1),
        tone: "success",
      };
    case "bounced":
    case "complained":
    case "suppressed":
    case "failed":
      return {
        label: value.charAt(0).toUpperCase() + value.slice(1),
        tone: "danger",
      };
    default:
      return { label: value, tone: "warning" };
  }
}

function suggestionStatus(suggestion: RecoverySuggestion) {
  const details: string[] = [];
  if (suggestion.authAccountCount === 0) {
    details.push("Login missing");
  } else if (suggestion.authAccountCount > 1) {
    details.push("Multiple logins");
  } else if (suggestion.emailVerified === true) {
    details.push("Email verified");
  } else {
    details.push("Awaiting verification");
  }

  if (suggestion.profileCount === 0) {
    details.push("Profile missing");
  } else if (suggestion.profileCount > 1) {
    details.push("Multiple profiles");
  } else {
    details.push("Profile linked");
  }
  if (suggestion.stagedSignupCount > 0) {
    details.push("Signup details available");
  }
  return details.join(" · ");
}

function StatusBadge({ label, tone }: { label: string; tone: Tone }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        tone === "success" &&
          "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200",
        tone === "warning" &&
          "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200",
        tone === "danger" &&
          "border-red-300 bg-red-50 text-red-800 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200",
        tone === "neutral" && "border-slate-300 bg-slate-50 text-slate-700",
      )}
    >
      {label}
    </Badge>
  );
}

function StateItem({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: Tone;
}) {
  return (
    <div className="min-w-0 space-y-1">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd>
        <StatusBadge label={value} tone={tone} />
      </dd>
      <dd className="text-xs text-muted-foreground">{detail}</dd>
    </div>
  );
}

async function copyWithFallback(title: string, value: string) {
  try {
    await copyTextToClipboard(value);
  } catch {
    if (typeof window !== "undefined") window.prompt(title, value);
  }
}

export function AccountRecoveryCard({ authUserId }: { authUserId?: string }) {
  const [email, setEmail] = React.useState("");
  const [result, setResult] = React.useState<RecoveryResult | null>(null);
  const [suggestionState, setSuggestionState] = React.useState<SuggestionState>(
    { query: "", status: "idle", items: [] },
  );
  const [emailFocused, setEmailFocused] = React.useState(false);
  const [suggestionsDismissed, setSuggestionsDismissed] =
    React.useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] =
    React.useState(-1);
  const [busy, setBusy] = React.useState<BusyAction>(null);
  const [confirmation, setConfirmation] =
    React.useState<ConfirmationAction>(null);
  const [profileEditorOpen, setProfileEditorOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const confirmationTrigger = React.useRef<HTMLButtonElement | null>(null);
  const suggestionRequest = React.useRef(0);
  const suggestionListId = React.useId();

  const inspectAccount = useAction(api.accountRecovery.inspect);
  const suggestAccountsAction = useAction(
    api.accountRecovery.suggestAccounts,
  );
  const suggestAccountsActionRef = React.useRef(suggestAccountsAction);
  suggestAccountsActionRef.current = suggestAccountsAction;
  const resendVerification = useAction(
    api.accountRecovery.resendVerificationEmail,
  );
  const generateVerificationLink = useAction(
    api.accountRecovery.generateVerificationLink,
  );
  const markEmailVerified = useAction(api.accountRecovery.markEmailVerified);
  const repairProfile = useAction(
    api.accountRecovery.repairMissingCustomerProfile,
  );
  const sendPasswordReset = useAction(api.admin.sendPasswordReset);
  const generatePasswordResetLink = useAction(
    api.admin.generatePasswordResetLink,
  );
  const isBusy = busy !== null;
  const normalizedSuggestionSearch = email.trim().toLowerCase();

  React.useEffect(() => {
    const requestId = suggestionRequest.current + 1;
    suggestionRequest.current = requestId;
    setActiveSuggestionIndex(-1);

    if (
      !authUserId ||
      result ||
      suggestionsDismissed ||
      normalizedSuggestionSearch.length < 2
    ) {
      setSuggestionState({
        query: normalizedSuggestionSearch,
        status: "idle",
        items: [],
      });
      return;
    }

    setSuggestionState({
      query: normalizedSuggestionSearch,
      status: "loading",
      items: [],
    });
    const timer = window.setTimeout(async () => {
      try {
        const response = (await suggestAccountsActionRef.current({
          authUserId,
          search: normalizedSuggestionSearch,
        })) as { suggestions?: RecoverySuggestion[] };
        if (suggestionRequest.current !== requestId) return;
        const items = Array.isArray(response?.suggestions)
          ? response.suggestions
          : [];
        setSuggestionState({
          query: normalizedSuggestionSearch,
          status: "ready",
          items,
        });
        setActiveSuggestionIndex(items.length > 0 ? 0 : -1);
      } catch {
        if (suggestionRequest.current !== requestId) return;
        setSuggestionState({
          query: normalizedSuggestionSearch,
          status: "error",
          items: [],
        });
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      if (suggestionRequest.current === requestId) {
        suggestionRequest.current += 1;
      }
    };
  }, [
    authUserId,
    normalizedSuggestionSearch,
    result,
    suggestionsDismissed,
  ]);

  const search = React.useCallback(
    async (target: string) => {
      if (!authUserId) {
        toast.error("Your admin session is not ready. Refresh and try again.");
        return null;
      }
      if (!target.trim()) {
        toast.error("Enter the customer's email address");
        return null;
      }
      suggestionRequest.current += 1;
      setSuggestionsDismissed(true);
      setSuggestionState({ query: "", status: "idle", items: [] });
      setBusy("search");
      try {
        const next = (await inspectAccount({
          authUserId,
          email: target.trim(),
        })) as RecoveryResult;
        setEmail(next.normalizedEmail);
        setResult(next);
        setHistoryOpen(false);
        return next;
      } catch (error) {
        setResult(null);
        toast.error(message(error, "Could not inspect this account"));
        return null;
      } finally {
        setBusy(null);
      }
    },
    [authUserId, inspectAccount],
  );

  async function handleResend() {
    if (!authUserId || !result) return;
    const target = result.normalizedEmail;
    setConfirmation(null);
    setBusy("resend");
    try {
      const response = (await resendVerification({
        authUserId,
        email: target,
      })) as { state: RecoveryResult | null; auditRecorded: boolean };
      setResult(response.state);
      if (response.auditRecorded) {
        toast.success(`Verification email requested for ${target}`);
      } else {
        toast.warning(
          "Email was sent, but its audit entry failed. Do not retry; search again and report the audit failure.",
        );
      }
    } catch (error) {
      toast.error(message(error, "Failed to send verification email"));
    } finally {
      setBusy(null);
    }
  }

  async function handleVerificationLink() {
    if (!authUserId || !result) return;
    const target = result.normalizedEmail;
    setBusy("verification-link");
    try {
      const response = (await generateVerificationLink({
        authUserId,
        email: target,
      })) as { verificationUrl: string; auditRecorded: boolean };
      await copyWithFallback(
        "Copy one-hour verification link",
        response.verificationUrl,
      );
      if (response.auditRecorded) {
        toast.success(`One-hour verification link copied for ${target}`);
      } else {
        toast.warning(
          "Link was created, but its audit entry failed. Do not create another link; report the audit failure.",
        );
      }
    } catch (error) {
      toast.error(message(error, "Failed to create verification link"));
    } finally {
      setBusy(null);
    }
  }

  async function handleRepair() {
    if (!authUserId || !result) return;
    const target = result.normalizedEmail;
    setConfirmation(null);
    setBusy("repair");
    try {
      const response = (await repairProfile({
        authUserId,
        email: target,
        confirmationEmail: target,
      })) as {
        changed: boolean;
        state: RecoveryResult | null;
        auditRecorded: boolean;
      };
      setResult(response.state);
      if (response.auditRecorded) {
        toast.success(
          response.changed
            ? `Customer profile created for ${target}`
            : `Customer profile already exists for ${target}`,
        );
      } else {
        toast.warning(
          "Profile was repaired, but its audit entry failed. Do not retry; search again and report the audit failure.",
        );
      }
    } catch (error) {
      toast.error(message(error, "Failed to repair customer profile"));
    } finally {
      setBusy(null);
    }
  }

  async function handleMarkVerified() {
    if (!authUserId || !result) return;
    const target = result.normalizedEmail;
    setConfirmation(null);
    setBusy("mark-verified");
    try {
      const response = (await markEmailVerified({
        authUserId,
        email: target,
        confirmationEmail: target,
      })) as {
        changed: boolean;
        state: RecoveryResult | null;
        auditRecorded: boolean;
      };
      setResult(response.state);
      if (response.auditRecorded) {
        toast.success(
          response.changed
            ? `Email marked verified for ${target}`
            : `${target} was already verified`,
        );
      } else {
        toast.warning(
          "Email was marked verified and its audit intent was saved, but the audit completion status failed. Do not retry; report the audit failure.",
        );
      }
    } catch (error) {
      toast.error(message(error, "Failed to mark email as verified"));
    } finally {
      setBusy(null);
    }
  }

  async function handlePasswordEmail() {
    if (!authUserId || !result) return;
    const target = result.normalizedEmail;
    setConfirmation(null);
    setBusy("password-email");
    try {
      await sendPasswordReset({ authUserId, email: target });
      toast.success(`Password reset email requested for ${target}`);
      await search(target);
    } catch (error) {
      toast.error(message(error, "Failed to send password reset email"));
    } finally {
      setBusy(null);
    }
  }

  async function handlePasswordLink() {
    if (!authUserId || !result) return;
    const target = result.normalizedEmail;
    setBusy("password-link");
    try {
      const response = (await generatePasswordResetLink({
        authUserId,
        email: target,
      })) as { resetUrl: string };
      await copyWithFallback(
        "Copy seven-day password reset link",
        response.resetUrl,
      );
      toast.success(`Seven-day password reset link copied for ${target}`);
    } catch (error) {
      toast.error(message(error, "Failed to create password reset link"));
    } finally {
      setBusy(null);
    }
  }

  const confirmationCopy =
    confirmation === "mark-verified"
      ? {
          title: "Mark this email as verified?",
          description: `This bypasses the customer's verification link for ${result?.normalizedEmail ?? "this account"}. Continue only after you personally confirmed that the customer owns this email through a trusted channel. Your admin account and the time of this override will be recorded.`,
          label: "I verified ownership",
          run: handleMarkVerified,
        }
      : confirmation === "repair"
        ? {
            title: "Create the missing customer profile?",
            description: `This creates the customer record for ${result?.normalizedEmail ?? "this account"} from its authentication account and staged signup details. It starts normal CRM, sales follow-up, and Stripe setup, but does not mark the email verified.`,
            label: "Create customer profile",
            run: handleRepair,
          }
        : confirmation === "password-email"
          ? {
              title: "Send a password reset email?",
              description: `A password reset email will be sent to ${result?.normalizedEmail ?? "this customer"}. Provider acceptance does not prove inbox delivery.`,
              label: "Send password reset",
              run: handlePasswordEmail,
            }
          : {
              title: "Resend the verification email?",
              description: `A new verification email will be sent to ${result?.normalizedEmail ?? "this customer"}. The customer must open the link to prove ownership of the address.`,
              label: "Send verification",
              run: handleResend,
            };

  const authUser = result?.authUsers[0] ?? null;
  const profile = result?.userProfiles[0] ?? null;
  const pending = result?.pendingProfiles[0] ?? null;
  const blockingIssues = result?.summary.issues ?? [];
  const hasAccountActions = Boolean(
    result?.summary.canRepairProfile ||
    result?.summary.canUsePasswordRecovery ||
    (result?.summary.profileLinked && profile),
  );
  const currentSuggestions =
    suggestionState.query === normalizedSuggestionSearch
      ? suggestionState.items
      : [];
  const showSuggestions =
    emailFocused &&
    !suggestionsDismissed &&
    !result &&
    normalizedSuggestionSearch.length >= 2 &&
    suggestionState.query === normalizedSuggestionSearch;
  const activeSuggestionId =
    showSuggestions && activeSuggestionIndex >= 0
      ? `${suggestionListId}-option-${activeSuggestionIndex}`
      : undefined;
  const suggestionAnnouncement = !showSuggestions
    ? ""
    : suggestionState.status === "loading"
      ? "Searching recovery accounts"
      : suggestionState.status === "error"
        ? "Recovery account suggestions are unavailable"
        : `${currentSuggestions.length} matching recovery ${currentSuggestions.length === 1 ? "account" : "accounts"}`;

  function selectSuggestion(suggestion: RecoverySuggestion) {
    setEmail(suggestion.email);
    setSuggestionsDismissed(true);
    setSuggestionState({ query: "", status: "idle", items: [] });
    setActiveSuggestionIndex(-1);
    void search(suggestion.email);
  }

  function openConfirmation(
    action: Exclude<ConfirmationAction, null>,
    event: React.MouseEvent<HTMLButtonElement>,
  ) {
    confirmationTrigger.current = event.currentTarget;
    setConfirmation(action);
  }

  return (
    <>
      <Card
        id="account-recovery"
        role="region"
        aria-labelledby="account-recovery-title"
        tabIndex={-1}
        className="mx-auto max-w-5xl scroll-mt-6"
      >
        <CardHeader>
          <CardTitle>
            <h2
              id="account-recovery-title"
              className="flex items-center gap-2 text-sm"
            >
              <UserRoundCog className="h-4 w-4" /> Account Recovery
            </h2>
          </CardTitle>
          <CardDescription>
            Find login accounts missing from Customer Access, inspect email
            delivery, and use guarded recovery actions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              void search(email);
            }}
          >
            <div className="relative min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="account-recovery-email">Customer email</Label>
              <Input
                id="account-recovery-email"
                type="email"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showSuggestions}
                aria-controls={suggestionListId}
                aria-activedescendant={activeSuggestionId}
                aria-busy={
                  showSuggestions && suggestionState.status === "loading"
                }
                placeholder="customer@example.com"
                value={email}
                disabled={isBusy}
                onFocus={() => {
                  setEmailFocused(true);
                  setSuggestionsDismissed(false);
                }}
                onBlur={() => setEmailFocused(false)}
                onKeyDown={(event) => {
                  if (event.key === "Escape" && showSuggestions) {
                    event.preventDefault();
                    setSuggestionsDismissed(true);
                    return;
                  }
                  if (!showSuggestions || currentSuggestions.length === 0) {
                    return;
                  }
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setActiveSuggestionIndex((current) =>
                      current >= currentSuggestions.length - 1
                        ? 0
                        : current + 1,
                    );
                  } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    setActiveSuggestionIndex((current) =>
                      current <= 0
                        ? currentSuggestions.length - 1
                        : current - 1,
                    );
                  } else if (
                    event.key === "Enter" &&
                    activeSuggestionIndex >= 0
                  ) {
                    event.preventDefault();
                    const suggestion =
                      currentSuggestions[activeSuggestionIndex];
                    if (suggestion) selectSuggestion(suggestion);
                  }
                }}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setResult(null);
                  setSuggestionsDismissed(false);
                }}
              />
              <span className="sr-only" role="status" aria-live="polite">
                {suggestionAnnouncement}
              </span>
              {showSuggestions && (
                <div
                  id={suggestionListId}
                  role="listbox"
                  aria-label="Matching recovery accounts"
                  className="absolute top-full z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
                >
                  {suggestionState.status === "loading" ? (
                    <div
                      role="option"
                      aria-disabled="true"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
                    >
                      <Loader2 className="h-4 w-4 animate-spin" /> Searching
                      login accounts...
                    </div>
                  ) : suggestionState.status === "error" ? (
                    <div
                      role="option"
                      aria-disabled="true"
                      className="px-3 py-2 text-sm text-muted-foreground"
                    >
                      Suggestions are unavailable. You can still enter the
                      exact email.
                    </div>
                  ) : currentSuggestions.length === 0 ? (
                    <div
                      role="option"
                      aria-disabled="true"
                      className="px-3 py-2 text-sm text-muted-foreground"
                    >
                      No recovery accounts start with “{email.trim()}”.
                    </div>
                  ) : (
                    currentSuggestions.map((suggestion, index) => (
                      <div
                        key={suggestion.email}
                        id={`${suggestionListId}-option-${index}`}
                        role="option"
                        aria-selected={index === activeSuggestionIndex}
                        className={cn(
                          "cursor-default rounded-sm px-3 py-2",
                          index === activeSuggestionIndex && "bg-muted",
                        )}
                        onPointerMove={() => setActiveSuggestionIndex(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectSuggestion(suggestion)}
                      >
                        <p className="break-all text-sm font-medium">
                          {suggestion.email}
                        </p>
                        {(suggestion.name || suggestion.company) && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {[suggestion.name, suggestion.company]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        )}
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {suggestionStatus(suggestion)}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <Button
              type="submit"
              variant="outline"
              disabled={isBusy || !authUserId || !email.trim()}
            >
              {busy === "search" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Search />
              )}
              {busy === "search" ? "Checking..." : "Check account"}
            </Button>
          </form>

          {!result && busy !== "search" && (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <ShieldCheck className="mx-auto h-5 w-5 text-muted-foreground" />
              <p className="mt-2 text-sm font-medium">
                Start typing the signup email
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Choose a suggested account or enter the exact email. This also
                finds logins that never completed customer-profile setup.
              </p>
            </div>
          )}
          {busy === "search" && !result && (
            <div
              role="status"
              className="flex items-center justify-center gap-2 rounded-lg border border-dashed p-8 text-sm text-muted-foreground"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Checking account and
              email history...
            </div>
          )}

          {result && (
            <div className="space-y-5" aria-live="polite">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                <div className="min-w-0">
                  <p className="break-all text-sm font-medium">
                    {result.normalizedEmail}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {authUser?.name ||
                      profile?.name ||
                      "No account name recorded"}
                    {profile?.company ? ` · ${profile.company}` : ""}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => void search(result.normalizedEmail)}
                >
                  <RefreshCw /> Refresh
                </Button>
              </div>

              <dl className="grid gap-4 rounded-lg border bg-muted/20 p-4 sm:grid-cols-2 lg:grid-cols-4">
                <StateItem
                  label="Authentication account"
                  value={
                    result.summary.authUserCount === 1
                      ? "Found"
                      : result.summary.authUserCount === 0
                        ? "Missing"
                        : "Multiple"
                  }
                  tone={
                    result.summary.authUserCount === 1 ? "success" : "danger"
                  }
                  detail={
                    authUser
                      ? `Created ${formatDate(authUser.createdAt)}`
                      : "No Better Auth user found"
                  }
                />
                <StateItem
                  label="Email verification"
                  value={
                    result.summary.authUserCount !== 1
                      ? "Unavailable"
                      : result.summary.emailVerified
                        ? "Verified"
                        : "Awaiting"
                  }
                  tone={result.summary.emailVerified ? "success" : "warning"}
                  detail={
                    result.summary.emailVerified
                      ? "Email ownership confirmed"
                      : "Customer or admin confirmation required"
                  }
                />
                <StateItem
                  label="Customer profile"
                  value={
                    result.summary.profileLinked
                      ? "Linked"
                      : result.summary.userProfileCount === 0
                        ? "Missing"
                        : "Needs review"
                  }
                  tone={result.summary.profileLinked ? "success" : "warning"}
                  detail={
                    result.summary.profileLinked
                      ? "Visible in Customer Access"
                      : "Not safely linked to this login"
                  }
                />
                <StateItem
                  label="Staged signup details"
                  value={
                    result.summary.pendingProfileCount === 0
                      ? "None"
                      : result.summary.pendingProfileCount === 1
                        ? "Available"
                        : "Multiple"
                  }
                  tone={
                    result.summary.pendingProfileCount > 1
                      ? "danger"
                      : "neutral"
                  }
                  detail={
                    pending
                      ? `${[pending.firstName, pending.lastName].filter(Boolean).join(" ") || "Name not recorded"}${pending.companyName ? ` · ${pending.companyName}` : ""}`
                      : "No pending signup metadata"
                  }
                />
              </dl>

              {blockingIssues.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100">
                  <div className="flex gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Recovery findings</p>
                      <ul className="mt-1 list-disc space-y-1 pl-4 text-xs">
                        {blockingIssues.map((issue) => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {result.summary.canMarkEmailVerified && (
                <section
                  aria-labelledby="email-verification-actions"
                  className="rounded-lg border border-amber-300 bg-amber-50/70 p-4 dark:border-amber-800/60 dark:bg-amber-950/20"
                >
                  <h3
                    id="email-verification-actions"
                    className="text-sm font-semibold"
                  >
                    Email ownership needs confirmation
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Prefer a customer-opened verification link. If you already
                    confirmed ownership through a trusted channel, you can
                    record that admin verification here.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {result.summary.canGenerateVerificationLink && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          disabled={
                            isBusy || !result.summary.canResendVerification
                          }
                          onClick={(event) => openConfirmation("resend", event)}
                        >
                          <Mail />
                          {result.summary.resendWaitSeconds > 0
                            ? `Resend in ${result.summary.resendWaitSeconds}s`
                            : "Resend verification email"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isBusy}
                          onClick={() => void handleVerificationLink()}
                        >
                          <Clipboard /> Copy 1-hour verification link
                        </Button>
                      </>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isBusy}
                      onClick={(event) =>
                        openConfirmation("mark-verified", event)
                      }
                    >
                      {busy === "mark-verified" ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <BadgeCheck />
                      )}
                      {busy === "mark-verified"
                        ? "Marking verified..."
                        : "Mark email as verified"}
                    </Button>
                  </div>
                </section>
              )}

              {hasAccountActions && (
                <section aria-labelledby="recovery-actions">
                  <h3 id="recovery-actions" className="text-xs font-semibold">
                    Account actions
                  </h3>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Only actions safe for the current account state are shown.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.summary.canRepairProfile && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={isBusy}
                        onClick={(event) => openConfirmation("repair", event)}
                      >
                        <UserRoundCog /> Create missing customer profile
                      </Button>
                    )}
                    {result.summary.canUsePasswordRecovery && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isBusy}
                          onClick={(event) =>
                            openConfirmation("password-email", event)
                          }
                        >
                          <Mail /> Send password reset email
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isBusy}
                          onClick={() => void handlePasswordLink()}
                        >
                          <KeyRound /> Copy 7-day password reset link
                        </Button>
                      </>
                    )}
                    {result.summary.profileLinked && profile && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={isBusy}
                        onClick={() => setProfileEditorOpen(true)}
                      >
                        <SquarePen /> Edit customer profile
                      </Button>
                    )}
                  </div>
                  {result.summary.canUsePasswordRecovery && (
                    <p className="mt-2 flex gap-1.5 text-xs text-muted-foreground">
                      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      Confirm the customer's identity before sharing copied
                      links.
                    </p>
                  )}
                </section>
              )}

              <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
                <section aria-labelledby="email-history-label">
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto w-full justify-between border px-3 py-3 text-start"
                      aria-label={`${historyOpen ? "Hide" : "Show"} authentication email history (${result.emailEvents.length} ${result.emailEvents.length === 1 ? "event" : "events"})`}
                    >
                      <span className="min-w-0">
                        <span
                          id="email-history-label"
                          className="block text-xs font-semibold"
                        >
                          Authentication email history
                        </span>
                        <span className="mt-0.5 block whitespace-normal text-xs font-normal text-muted-foreground">
                          Delivery details and provider responses
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <Badge variant="secondary">
                          {result.emailEvents.length}
                        </Badge>
                        <ChevronDown
                          className={cn(historyOpen && "rotate-180")}
                        />
                      </span>
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    <p className="mb-2 text-xs text-muted-foreground">
                      “Provider accepted” means the provider received the
                      message; it does not prove inbox delivery.
                    </p>
                    {result.emailEvents.length === 0 ? (
                      <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                        No authentication email events recorded for this
                        address.
                      </div>
                    ) : (
                      <div className="divide-y rounded-lg border">
                        {result.emailEvents.map((event) => {
                          const status = deliveryStatus(event.status);
                          return (
                            <div
                              key={event._id}
                              className="flex flex-col gap-2 p-3 sm:flex-row sm:justify-between"
                            >
                              <div className="min-w-0">
                                <p className="break-words text-xs font-medium">
                                  {event.subject}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {event.emailType.replaceAll("_", " ")} ·{" "}
                                  {formatDate(event.createdAt)}
                                </p>
                                {event.error && (
                                  <p className="mt-1 break-words text-xs text-red-700 dark:text-red-300">
                                    {event.error}
                                  </p>
                                )}
                              </div>
                              <StatusBadge
                                label={status.label}
                                tone={status.tone}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CollapsibleContent>
                </section>
              </Collapsible>
            </div>
          )}
        </CardContent>
      </Card>

      {result && profile && (
        <CustomerProfileEditDialog
          open={profileEditorOpen}
          onOpenChange={setProfileEditorOpen}
          authUserId={authUserId}
          loginEmail={result.normalizedEmail}
          profile={profile}
          onSaved={async () => {
            setProfileEditorOpen(false);
            await search(result.normalizedEmail);
          }}
        />
      )}

      <AlertDialog
        open={confirmation !== null}
        onOpenChange={(open) => {
          if (!open && !isBusy) {
            setConfirmation(null);
            window.requestAnimationFrame(() =>
              confirmationTrigger.current?.focus(),
            );
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmationCopy.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmationCopy.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isBusy}
              onClick={() => void confirmationCopy.run()}
            >
              {confirmationCopy.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
